import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import {
  ArrowClockwise, CheckCircle, XCircle, Users, Database,
  Lightning, Pulse, ShieldCheck, Robot,
} from '@phosphor-icons/react';
import SystemHealth from './SystemHealth';

function StatCard({ icon: Icon, color, label, value, sub }) {
  return (
    <div className={`jp-card p-5 border-l-4 border-l-${color}-500`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={`text-${color}-500`} />
        <span className="text-sm font-semibold text-white">{label}</span>
      </div>
      <div className="text-3xl font-mono text-white">{value ?? '—'}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function FlagBadge({ label, enabled }) {
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${enabled ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-zinc-700 bg-white/[0.02]'}`}>
      <span className="text-xs text-zinc-400">{label}</span>
      {enabled
        ? <CheckCircle size={14} className="text-emerald-400" weight="fill" />
        : <XCircle size={14} className="text-zinc-600" weight="fill" />}
    </div>
  );
}

function ConnPill({ label, ok, note }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${ok ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-red-500/30 text-red-400 bg-red-500/5'}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`} />
      {label}{note ? ` (${note})` : ''}
    </div>
  );
}

export default function AdminHealthTab() {
  const { fetchAdminTelemetry, fetchSkillGraphStats, rebuildSkillGraph, fetchCeleryHealth } = useApp();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [graphStats, setGraphStats] = useState(null);
  const [celeryHealth, setCeleryHealth] = useState(null);
  const [rebuildingId, setRebuildingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, g, c] = await Promise.all([
        fetchAdminTelemetry('health'),
        fetchSkillGraphStats(),
        fetchCeleryHealth(),
      ]);
      setHealth(h);
      setGraphStats(g);
      setCeleryHealth(c);
    } finally {
      setLoading(false);
    }
  }, [fetchAdminTelemetry, fetchSkillGraphStats, fetchCeleryHealth]);

  const onRebuild = useCallback(async (pid) => {
    setRebuildingId(pid);
    try {
      await rebuildSkillGraph(pid);
      const g = await fetchSkillGraphStats();
      setGraphStats(g);
    } finally {
      setRebuildingId(null);
    }
  }, [rebuildSkillGraph, fetchSkillGraphStats]);

  useEffect(() => { load(); }, [load]);

  const flags = health?.v72_features || {};
  const users = health?.collections?.users?.documents ?? 0;
  const jobs = health?.total_jobs ?? 0;
  const totalDocs = health?.total_documents ?? 0;
  const overallStatus = health?.status || 'unknown';

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Pulse size={24} className="text-amber-500" />
          <h2 className="text-sm font-bold text-white">System Health</h2>
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
            overallStatus === 'healthy'  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
            overallStatus === 'degraded' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                           'bg-red-500/10 text-red-400 border-red-500/20'
          }`}>{overallStatus}</span>
        </div>
        <button onClick={load} className={`p-2 hover:bg-white/5 rounded-full transition-colors ${loading ? 'animate-spin' : ''}`}>
          <ArrowClockwise size={16} />
        </button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={Users}    color="amber"   label="Registered Users" value={users}    sub="Active accounts" />
        <StatCard icon={Database} color="emerald" label="Jobs Indexed"      value={jobs}     sub="In Postgres jobs table" />
        <StatCard icon={Lightning} color="sky"    label="Total Documents"   value={totalDocs} sub={`Across ${health?.total_collections ?? '…'} tables`} />
      </div>

      {/* Feature flags + service connectivity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="jp-card p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <Lightning size={12} /> v7.2 Feature Flags
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <FlagBadge label="System Logging"  enabled={flags.system_logging} />
            <FlagBadge label="Circuit Breaker" enabled={flags.circuit_breaker} />
            <FlagBadge label="Cache Service"   enabled={flags.cache_service} />
            <FlagBadge label="Query Profiler"  enabled={flags.query_profiler} />
          </div>
        </div>

        <div className="jp-card p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <ShieldCheck size={12} /> Infrastructure Connectivity
          </h3>
          {!health?.celery?.alive && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium">
              <XCircle size={14} weight="fill" />
              Celery workers are down — job searches will queue but not run
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <ConnPill label="Postgres" ok={health?.db === 'connected'} />
            <ConnPill label="Redis"    ok={health?.redis?.connected ?? false} note={health?.cache_backend} />
            <ConnPill label="pgvector" ok={health?.db === 'connected'} note={health?.chromadb?.connected ? 'chroma fallback ready' : undefined} />
            <ConnPill
              label="Celery"
              ok={health?.celery?.alive ?? false}
              note={health?.celery?.alive ? `${health.celery.worker_count} worker${health.celery.worker_count !== 1 ? 's' : ''}` : 'no workers'}
            />
            {Object.entries(health?.llm_providers || {}).map(([name, status]) => (
              <ConnPill key={name} label={name.toUpperCase()} ok={status === 'healthy'} note={status !== 'healthy' ? status : undefined} />
            ))}
          </div>
          {health?.disk && (
            <div className="mt-4">
              <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
                <span>OCI Disk</span>
                <span>{health.disk.used_gb}GB / {health.disk.total_gb}GB</span>
              </div>
              <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${health.disk.used_percent > 85 ? 'bg-red-500' : health.disk.used_percent > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${health.disk.used_percent}%` }}
                />
              </div>
              <div className="text-[10px] text-zinc-600 mt-0.5">{health.disk.used_percent}% used</div>
            </div>
          )}
        </div>
      </div>

      {/* Worker Health (v7.5) */}
      <div className="jp-card p-5">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
          <Lightning size={12} /> Celery Worker Health
        </h3>
        {!celeryHealth ? (
          <div className="text-xs text-zinc-500">Loading worker telemetry…</div>
        ) : (
          <>
            {!celeryHealth.alive && (celeryHealth.queues?.search_queue_depth > 0 || celeryHealth.queues?.scoring_queue_depth > 0) && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium">
                <XCircle size={14} weight="fill" />
                {(celeryHealth.queues.search_queue_depth || 0) + (celeryHealth.queues.scoring_queue_depth || 0)} tasks stuck in Redis — workers not consuming
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border border-zinc-800 bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Search Pool</div>
                <div className="text-lg font-mono text-white">{celeryHealth.search_workers?.alive_count ?? 0} worker{celeryHealth.search_workers?.alive_count === 1 ? '' : 's'}</div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  active {celeryHealth.search_workers?.active_tasks ?? 0} · reserved {celeryHealth.search_workers?.reserved_tasks ?? 0} · queue {celeryHealth.queues?.search_queue_depth ?? '—'}
                </div>
              </div>
              <div className="p-3 rounded-lg border border-zinc-800 bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Scoring Pool</div>
                <div className="text-lg font-mono text-white">{celeryHealth.scoring_workers?.alive_count ?? 0} worker{celeryHealth.scoring_workers?.alive_count === 1 ? '' : 's'}</div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  active {celeryHealth.scoring_workers?.active_tasks ?? 0} · reserved {celeryHealth.scoring_workers?.reserved_tasks ?? 0} · queue {celeryHealth.queues?.scoring_queue_depth ?? '—'}
                </div>
              </div>
            </div>
            <div className="mt-3 text-[10px] text-zinc-600">
              Last successful search: {celeryHealth.last_successful_search
                ? new Date(celeryHealth.last_successful_search).toLocaleString('en-IN')
                : '—'}
            </div>
          </>
        )}
      </div>

      {/* Scraper / data-source connectivity */}
      <div className="jp-card p-5">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
          <Robot size={12} /> Scraper &amp; Source Connectivity
        </h3>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'jobspy',      label: 'JobSpy'      },
            { key: 'ddgs',        label: 'DuckDuckGo'  },
            { key: 'crawl4ai',    label: 'Crawl4AI'    },
            { key: 'jina_reader', label: 'Jina Reader' },
          ].map(({ key, label }) => {
            const ok = health?.scrapers?.[key] ?? false;
            const note = key === 'crawl4ai' ? 'in-process library' : key === 'jina_reader' ? 'public service (r.jina.ai)' : null;
            return (
              <ConnPill
                key={key}
                label={label}
                ok={ok}
                note={note || undefined}
              />
            );
          })}
          {/* LLM providers also shown here for quick cross-reference */}
          {Object.entries(health?.llm_providers || {}).map(([name, status]) => (
            <ConnPill key={`llm-${name}`} label={name.toUpperCase()} ok={status === 'healthy'} note={status !== 'healthy' ? status : undefined} />
          ))}
        </div>
        <p className="text-[10px] text-zinc-700 mt-3">
          JobSpy / DuckDuckGo: Python package import check. Crawl4AI / Jina Reader: environment variable presence.
          Toggle sources on/off in the <strong className="text-zinc-500">Cost</strong> tab.
        </p>

        {/* v7.7.0 P2-5 — per-scraper circuit-breaker state */}
        {Array.isArray(health?.scraper_circuit_breakers) && health.scraper_circuit_breakers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
              Scraper Circuit Breakers
            </div>
            <div className="flex flex-wrap gap-2">
              {health.scraper_circuit_breakers.map((cb) => {
                const name = (cb.provider || '').replace(/^scraper:/, '');
                const ok = cb.state === 'closed';
                const note = cb.state === 'open'
                  ? `open (${cb.failure_count} fails)`
                  : cb.state === 'half-open'
                    ? 'half-open probe'
                    : cb.failure_count > 0 ? `${cb.failure_count} fails` : undefined;
                return <ConnPill key={cb.provider} label={name} ok={ok} note={note} />;
              })}
            </div>
            <p className="text-[10px] text-zinc-700 mt-2">
              Open after 3 consecutive failures; auto-recovers after 30 minutes.
              Reset via <code className="text-zinc-500">POST /api/admin/circuit-breaker/reset?provider=scraper:&lt;name&gt;</code>.
            </p>
          </div>
        )}
      </div>

      {/* Intelligence Layer */}
      <div className="jp-card p-5">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
          <Lightning size={12} /> Intelligence Layer — Skill Graphs
        </h3>
        {!graphStats || !graphStats.stats?.length ? (
          <div className="text-xs text-zinc-500">No skill graphs built yet. Graphs build automatically after scored search runs.</div>
        ) : (
          <div className="space-y-2">
            {graphStats.stats.map((p) => (
              <div key={p.persona_id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-800 bg-white/[0.02]">
                <div className="flex items-center gap-4 text-xs">
                  <span className="font-mono text-zinc-400">{p.persona_id?.slice(0, 8) || '—'}</span>
                  <span className="text-zinc-300">{p.node_count} nodes</span>
                  <span className="text-zinc-300">{p.edge_count} edges</span>
                  <span className="text-zinc-500">{p.last_built_at ? new Date(p.last_built_at).toLocaleString() : '—'}</span>
                </div>
                <button
                  onClick={() => onRebuild(p.persona_id)}
                  disabled={rebuildingId === p.persona_id}
                  className="px-2 py-1 text-[10px] font-semibold uppercase border border-amber-500/30 text-amber-400 rounded hover:bg-amber-500/10 disabled:opacity-50"
                >
                  {rebuildingId === p.persona_id ? 'Rebuilding…' : 'Rebuild'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Architecture graph */}
      <SystemHealth />
    </div>
  );
}
